import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  ChevronRight,
  ChevronDown,
  Loader2,
  Check,
  X as XIcon,
  PackageCheck,
  Zap,
  RefreshCw,
} from 'lucide-react';

import { ErrorState } from '@/components/common/ErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fmt } from '@/lib/format';
import { useTenantLocale } from '@/hooks/useTenantLocale';
import {
  deliveryQuotesService,
  type DeliveryQuoteRow,
  type ListQuotesParams,
} from '@/services/deliveryQuotes';

type BoundFilter = 'all' | 'accepted' | 'open';

/**
 * Quotes & Audit — the end-to-end trace of every EDD + charge the portal was
 * shown at checkout. For any destination/date an operator can see which
 * couriers were considered, why each was kept or dropped, and which plan the
 * customer accepted into an order. The auditable backbone of the commitment.
 */
export function DeliveryQuotesPage() {
  const [pincode, setPincode] = useState('');
  const [appliedPincode, setAppliedPincode] = useState('');
  const [bound, setBound] = useState<BoundFilter>('all');

  const params: ListQuotesParams = {
    pincode: appliedPincode || undefined,
    bound: bound === 'accepted' ? true : bound === 'open' ? false : undefined,
    pageSize: 100,
  };

  const query = useQuery({
    queryKey: ['delivery-quotes', params],
    queryFn: () => deliveryQuotesService.list(params),
  });

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const acceptedCount = rows.filter((r) => r.salesOrderId).length;
  const liveCount = rows.filter((r) => r.options.some((o) => o.isLive)).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[13.5px] font-semibold text-foreground">Quotes &amp; audit</h2>
          <p className="text-[11.5px] text-muted-foreground">
            Every delivery quote we returned at checkout — the EDD + charges shown, and the
            full reasoning behind each. Trace any promise end-to-end.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
          <RefreshCw className={`h-3.5 w-3.5 ${query.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Quotes (filtered)" value={String(total)} />
        <Kpi label="Accepted to order" value={String(acceptedCount)} />
        <Kpi label="Live-priced" value={String(liveCount)} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={pincode}
            onChange={(e) => setPincode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setAppliedPincode(pincode.trim())}
            placeholder="Destination pincode"
            className="h-8 w-48 pl-8 text-[12.5px]"
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => setAppliedPincode(pincode.trim())}>
          Search
        </Button>
        {appliedPincode && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setPincode('');
              setAppliedPincode('');
            }}
          >
            Clear
          </Button>
        )}
        <div className="ml-auto inline-flex rounded-md border border-border bg-card p-0.5">
          {(['all', 'accepted', 'open'] as BoundFilter[]).map((b) => (
            <button
              key={b}
              onClick={() => setBound(b)}
              className={`rounded px-2.5 py-1 text-[11.5px] font-medium capitalize transition ${
                bound === b ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {b === 'all' ? 'All' : b === 'accepted' ? 'Accepted' : 'Open'}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 py-12 text-center text-[12.5px] text-muted-foreground">
          No delivery quotes yet{appliedPincode ? ` for pincode ${appliedPincode}` : ''}. Quotes
          are recorded automatically whenever the portal requests delivery options.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <QuoteRow key={r.id} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="text-[18px] font-semibold tabular-nums text-foreground">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function QuoteRow({ row }: { row: DeliveryQuoteRow }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-border bg-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{row.deliveryPincode ?? '—'}</span>
            {row.country && <span className="text-[11px] text-muted-foreground">{row.country}</span>}
            {row.weightGrams != null && (
              <span className="text-[11px] text-muted-foreground">· {row.weightGrams} g</span>
            )}
            {row.salesOrderId ? (
              <Badge variant="secondary" className="gap-1 text-[10px]">
                <PackageCheck className="h-3 w-3" /> Accepted
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                Not accepted
              </Badge>
            )}
            {row.servedFromCache && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                cached
              </Badge>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {row.options.map((o) => (
              <span key={o.tier} className="text-[11.5px] text-muted-foreground">
                <span className="font-medium text-foreground">{o.name}</span>{' '}
                {fmt.money(o.charge, row.currency)}
                {o.expectedDeliveryDate ? ` · ${fmt.date(o.expectedDeliveryDate)}` : ''}
                {o.courierName ? ` · ${o.courierName}` : ''}
                {o.isLive && <Zap className="ml-0.5 inline h-3 w-3 text-amber-500" />}
              </span>
            ))}
          </div>
        </div>
        <span className="shrink-0 text-[11px] text-muted-foreground">{fmt.dateTime(row.createdAt)}</span>
      </button>
      {open && <QuoteTrace id={row.id} />}
    </div>
  );
}

function QuoteTrace({ id }: { id: string }) {
  // Carrier shortlist rates are the RAW courier rates, denominated in the
  // tenant's base currency (what Shiprocket / DHL India quote in — INR), NOT
  // the customer's display currency. Format them with the base currency so an
  // INR rate on a USD order isn't mislabelled "$" (the option `charge` below
  // stays in the display currency — that one IS customer-facing).
  const baseCurrency = useTenantLocale().currency;
  const detail = useQuery({
    queryKey: ['delivery-quote', id],
    queryFn: () => deliveryQuotesService.get(id),
  });

  if (detail.isLoading) {
    return (
      <div className="flex items-center gap-2 border-t border-border/60 px-4 py-3 text-[11.5px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading trace…
      </div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <div className="border-t border-border/60 px-4 py-3 text-[11.5px] text-destructive">
        Could not load the quote trace.
      </div>
    );
  }

  return (
    <div className="space-y-3 border-t border-border/60 px-4 py-3">
      {detail.data.options.map((o) => (
        <div key={o.optionId} className="rounded-lg border border-border/60 bg-muted/20 p-3">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className="text-[12px] font-semibold text-foreground">{o.name}</span>
            <Badge variant="outline" className="text-[10px]">
              {o.trace?.policy === 'FASTEST' ? 'Fastest' : 'Cheapest'}
            </Badge>
            {o.trace?.maxEtaDays != null && (
              <span className="text-[10.5px] text-muted-foreground">SLA ≤ {o.trace.maxEtaDays}d</span>
            )}
            <span className="ml-auto text-[11.5px] text-muted-foreground">
              {fmt.money(o.charge, o.currency)}
              {o.expectedDeliveryDate ? ` · ${fmt.date(o.expectedDeliveryDate)}` : ''}
            </span>
          </div>
          {o.trace?.reason && (
            <p className="mb-1.5 text-[11.5px] text-muted-foreground">{o.trace.reason}</p>
          )}
          {o.trace?.shortlist && o.trace.shortlist.length > 0 ? (
            <div className="space-y-0.5">
              {o.trace.shortlist.map((c, i) => {
                const isChosen = c.courierId && c.courierId === o.chosen?.courierId;
                return (
                  <div
                    key={`${c.courierId ?? c.courierName}-${i}`}
                    className={`flex items-center gap-2 rounded px-2 py-1 text-[11px] ${
                      isChosen ? 'bg-success/10 text-foreground' : c.eligible ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {isChosen ? (
                      <Check className="h-3 w-3 shrink-0 text-success" />
                    ) : c.eligible ? (
                      <span className="h-3 w-3 shrink-0" />
                    ) : (
                      <XIcon className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                    )}
                    <span className="font-medium">{c.courierName ?? c.courierId ?? '—'}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {c.rate != null ? fmt.money(Number(c.rate), baseCurrency) : '—'}
                      {c.etaDays != null ? ` · ${c.etaDays}d` : c.etd ? ` · ${fmt.date(c.etd)}` : ''}
                      {c.rating != null ? ` · ${c.rating}★` : ''}
                    </span>
                    {!c.eligible && c.dropReason && (
                      <span className="ml-auto truncate text-[10.5px] italic text-muted-foreground/80">
                        {c.dropReason}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] italic text-muted-foreground">
              No live courier shortlist — static plan ETA was used.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
